import type { ClefDeCouleur, Couleur } from '@taki/data-model';

/**
 * LA TEINTE D'UN CRENEAU de « Ma semaine » (`docs/socle/couleur-photo-fiche.md`).
 *
 * Un creneau prend la couleur de SON PROJET quand le projet en a une (le champ
 * standard `couleur`, que le jeu des projets active). Sinon, une teinte est
 * tiree d'une graine, pour que deux creneaux du meme projet se reconnaissent et
 * que la couleur reste stable d'un affichage a l'autre.
 *
 * Le repli n'a AUCUN gris : l'ancien tirage melangeait `--mode-border`,
 * `--ink-500` et `--ink-700`, trois blocs presque identiques. Ici, seules les
 * teintes du cercle chromatique de la grille du design (sans les neutres
 * `neutral`, `slate` et `brown`, sans le rouge d'erreur `danger`, sans l'accent
 * ni le vert de succes, doublons de `blue` et `emerald`).
 */
export const TEINTES_DE_REPLI: readonly ClefDeCouleur[] = Object.freeze([
  'orange',
  'amber',
  'yellow',
  'olive',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'plum',
  'pink',
  'coral',
] as const);

/** Une graine de texte devenue un entier stable (le meme hash que l'ancien tirage). */
function hashDe(graine: string): number {
  return [...graine].reduce((value, char) => (value * 31 + char.charCodeAt(0)) >>> 0, 0);
}

/**
 * La couleur d'un creneau : celle de son projet si `couleurs` la connait,
 * sinon la teinte de repli tiree de la graine (l'id du projet, ou de la fiche
 * quand elle n'en a pas).
 */
export function couleurDuCreneau(
  projetId: string | null | undefined,
  idDeFiche: string,
  couleurs: ReadonlyMap<string, Couleur>,
): Couleur {
  const duProjet = projetId == null ? undefined : couleurs.get(projetId);
  if (duProjet !== undefined) return duProjet;
  const graine = projetId ?? idDeFiche;
  return TEINTES_DE_REPLI[hashDe(graine) % TEINTES_DE_REPLI.length] ?? 'blue';
}
